import type { ChatModel, ChatModelChange } from './ChatModel';
import { MEDIA_KEYS, mediaAssetUrl } from '../game/content/MediaUrls';

export const CHAT_RECEIVE_SOUND_URL = mediaAssetUrl(MEDIA_KEYS.chatReceive);
export const CHAT_SEND_SOUND_URL = mediaAssetUrl(MEDIA_KEYS.chatSend);

export interface ChatAudioRuntime {
  readonly existingContext: AudioContext | null;
  loadBuffer(url: string): Promise<AudioBuffer>;
  unlock(): Promise<void>;
}

export interface ChatAudioFeedbackOptions {
  readonly localAuthorId?: () => string | null;
  readonly receiveUrl?: string;
  readonly sendUrl?: string;
}

/** Plays one short chat cue at a time and ignores replayed history plus the local user's echoed messages. */
export class ChatAudioFeedback {
  readonly #runtime: ChatAudioRuntime;
  readonly #localAuthorId: () => string | null;
  readonly #receiveUrl: string;
  readonly #sendUrl: string;
  readonly #unsubscribe: () => void;
  #source: AudioBufferSourceNode | null = null;
  #generation = 0;
  #disposed = false;

  constructor(model: ChatModel, runtime: ChatAudioRuntime, options: ChatAudioFeedbackOptions = {}) {
    this.#runtime = runtime;
    this.#localAuthorId = options.localAuthorId ?? (() => null);
    this.#receiveUrl = options.receiveUrl ?? CHAT_RECEIVE_SOUND_URL;
    this.#sendUrl = options.sendUrl ?? CHAT_SEND_SOUND_URL;
    this.#unsubscribe = model.subscribe(this.#onModelChange);
  }

  async preload(): Promise<void> {
    if (this.#disposed) return;
    await Promise.all([
      this.#runtime.loadBuffer(this.#receiveUrl),
      this.#runtime.loadBuffer(this.#sendUrl),
    ]);
  }

  playSent(): void {
    void this.#play(this.#sendUrl);
  }

  /** Browser autoplay policy requires a user gesture before notification audio can start. */
  bindUnlockGestures(target: EventTarget): () => void {
    let active = true;
    const release = (): void => {
      if (!active) return;
      active = false;
      target.removeEventListener('pointerdown', unlock);
      target.removeEventListener('keydown', unlock);
    };
    const unlock = (): void => {
      void this.#runtime.unlock().then(release).catch(() => undefined);
    };
    target.addEventListener('pointerdown', unlock);
    target.addEventListener('keydown', unlock);
    return release;
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#generation += 1;
    this.#unsubscribe();
    this.#stopCurrent();
  }

  #onModelChange = (change: ChatModelChange): void => {
    if (change.kind !== 'message') return;
    const localAuthorId = this.#localAuthorId();
    if (localAuthorId && change.message.author?.id === localAuthorId) return;
    void this.#play(this.#receiveUrl);
  };

  async #play(url: string): Promise<void> {
    if (this.#disposed) return;
    const generation = ++this.#generation;
    this.#stopCurrent();
    let buffer: AudioBuffer;
    try {
      buffer = await this.#runtime.loadBuffer(url);
    } catch {
      return;
    }
    if (this.#disposed || generation !== this.#generation) return;
    const context = this.#runtime.existingContext;
    if (!context || context.state !== 'running') return;
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    source.onended = () => {
      if (this.#source === source) this.#source = null;
    };
    this.#source = source;
    source.start();
  }

  #stopCurrent(): void {
    const source = this.#source;
    this.#source = null;
    if (!source) return;
    source.onended = null;
    try { source.stop(); } catch { /* Already stopped or naturally ended. */ }
    source.disconnect();
  }
}
